import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Button } from '@viastud/ui/button'
import { useToast } from '@viastud/ui/hooks/use-toast'
import { trpc } from '@viastud/ui/lib/trpc'
import { useEffect } from 'react'

import { useAuthStore } from '@/store/auth.store'

export const Route = createFileRoute('/invitation/$token')({
  component: InvitationPage,
})

function InvitationPage() {
  const { token } = Route.useParams()
  const { toast } = useToast()
  const navigate = useNavigate()
  const auth = useAuthStore()

  const { data: invitation, isLoading, error } = trpc.user.getInvitationDetails.useQuery({ token })

  const respondMutation = trpc.user.respondToInvitation.useMutation({
    onSuccess: (_, variables) => {
      toast({
        title: variables.action === 'ACCEPT' ? 'Demande acceptée' : 'Demande refusée',
        description:
          variables.action === 'ACCEPT'
            ? 'Votre parent peut désormais suivre votre progression.'
            : 'La demande a été refusée.',
      })
      void navigate({ to: '/' })
    },
    onError: (error) => {
      if (error instanceof Error) {
        toast({
          title: 'Erreur',
          description: error.message ?? 'Une erreur est survenue.',
          variant: 'destructive',
        })
      }
    },
  })

  useEffect(() => {
    if (!auth.isAuthenticated) {
      void navigate({
        to: '/login',
        search: { from: `/invitation/${token}` },
      })
    }
  }, [auth.isAuthenticated, navigate, token])

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500" />
      </div>
    )
  }

  if (error || !invitation) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <h1 className="text-2xl font-bold text-gray-900">Invitation invalide</h1>
          <p className="mt-2 text-gray-600">
            Cette invitation est invalide, a expiré ou a déjà été traitée.
          </p>
          <Button className="mt-6" onClick={() => navigate({ to: '/' })}>
            Retour à l&#39;accueil
          </Button>
        </div>
      </div>
    )
  }

  const handleResponse = (action: 'ACCEPT' | 'REJECT') => {
    respondMutation.mutate({ token, action })
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md">
        <div className="shadow-custom flex flex-col gap-8 rounded-2xl border border-gray-100 bg-white p-8">
          <div className="flex justify-center">
            <img src="/logos/viastud-logo.svg" alt="Viastud" className="h-10" />
          </div>

          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-950">Demande de rattachement</h1>
            <p className="mt-4 text-gray-600">
              <span className="font-semibold text-gray-900">{invitation.parentName}</span> souhaite
              vous ajouter comme enfant sur son compte.
            </p>
            <p className="mt-4 text-sm italic text-gray-500">
              En acceptant, votre parent pourra consulter votre progression et vos résultats sur la
              plateforme.
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <Button
              className="w-full"
              onClick={() => {
                handleResponse('ACCEPT')
              }}
              disabled={respondMutation.isPending}
            >
              {respondMutation.isPending && respondMutation.variables?.action === 'ACCEPT'
                ? 'Acceptation...'
                : 'Autoriser'}
            </Button>
            <Button
              variant="outline"
              className="w-full text-gray-600"
              onClick={() => {
                handleResponse('REJECT')
              }}
              disabled={respondMutation.isPending}
            >
              {respondMutation.isPending && respondMutation.variables?.action === 'REJECT'
                ? 'Refus...'
                : 'Refuser'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
